import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Popover from '@mui/material/Popover';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import MenuItem, { menuItemClasses } from '@mui/material/MenuItem';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { BranchStatus, type BranchDto } from 'src/api/branch_api_data';

// ----------------------------------------------------------------------

export type Props = {
  row: BranchDto;
  selected: boolean;
  onSelectRow: () => void;
  onDeleteRow: () => void;
  onEditRow: () => void;
};

export function BranchTableRow({ row, selected, onSelectRow, onDeleteRow, onEditRow }: Props) {
  const navigate = useNavigate();
  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(null);

  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenPopover(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback(() => {
    setOpenPopover(null);
  }, []);

  const getStatusLabel = (status: BranchStatus | null | undefined) => {
    switch (status) {
      case BranchStatus.OPEN:
        return { text: 'Açık', color: 'success' as const };
      case BranchStatus.MAINTENANCE:
        return { text: 'Bakımda', color: 'warning' as const };
      case BranchStatus.CLOSED:
      default:
        return { text: 'Kapalı', color: 'error' as const };
    }
  };

  const { text: statusText, color: statusColor } = getStatusLabel(row.status);

  return (
    <>
      <TableRow hover selected={selected}>
        <TableCell padding="checkbox">
          <Checkbox disableRipple checked={selected} onChange={onSelectRow} />
        </TableCell>

        <TableCell>
          <Typography 
            variant="subtitle2" 
            noWrap
            sx={{ 
              cursor: 'pointer',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
            onClick={() => navigate(`/dashboard/branches/${row.id}`)}
          >
            {row.name}
          </Typography>
        </TableCell>

        <TableCell>{row.companyName || '-'}</TableCell>

        <TableCell>{row.address || '-'}</TableCell>

        <TableCell>{row.phone || '-'}</TableCell>

        <TableCell>
          {(() => {
            const responsibles = row.responsibleUsernames;
            
            // Debug log for this specific row
            console.log(`Rendering responsibles for "${row.name}":`, {
              responsibles,
              type: typeof responsibles,
              isArray: Array.isArray(responsibles),
              length: responsibles?.length,
              joinResult: responsibles?.join?.(', ')
            });
            
            // Render logic
            if (!responsibles || !Array.isArray(responsibles) || responsibles.length === 0) {
              return <span style={{ color: '#999' }}>-</span>;
            }
            
            const joinedResponsibles = responsibles.join(', ');
            return (
              <span 
                title={`${responsibles.length} sorumlu: ${joinedResponsibles}`}
                style={{ 
                  maxWidth: '200px', 
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {joinedResponsibles}
              </span>
            );
          })()}
        </TableCell>

        <TableCell>{row.branchOpeningTime || '-'}</TableCell>

        <TableCell>{row.branchClosingTime || '-'}</TableCell>

        <TableCell align="center">{row.dailyEmployeeCount ?? 0}</TableCell>

        <TableCell>
          <Label color={statusColor}>
            {statusText}
          </Label>
        </TableCell>

        <TableCell align="right">
          <IconButton onClick={handleOpenPopover}>
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            navigate(`/dashboard/branches/${row.id}`);
            handleClosePopover();
          }}
        >
          <Iconify icon="solar:eye-bold" />
          Detayları Görüntüle
        </MenuItem>

        <MenuItem
          onClick={() => {
            onEditRow();
            handleClosePopover();
          }}
        >
          <Iconify icon="solar:pen-bold" />
          Düzenle
        </MenuItem>

        <MenuItem
          onClick={() => {
            onDeleteRow();
            handleClosePopover();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          Sil
        </MenuItem>
      </Popover>
    </>
  );
}
